import _ from 'underscore';
import Promise from 'bluebird';
import React from 'react';
import { HelpIcon } from '@bingads-webui-campaign-react/help';

import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { parseRule } from './parser';
import { IffEditor } from './editor/iff-editor';
import { AudienceSelectorLegacyViewManager } from './editor/audience-selector';
import { IF_FUNCTION_TYPE } from './if-function-type';
import { escapeIffAudienceName, escapeIffInsertText, escapeIffDefaultText } from './escape';

const isSelectArrow = element =>
  _.isFunction(element.className.indexOf) && element.className.indexOf('arrow-button') !== -1 &&
  element.parentElement !== null &&
  _.isFunction(element.parentElement.className.indexOf) && element.parentElement.className.indexOf('dlbItem-actions') !== -1;

const isRemoveArrow = element =>
  _.isFunction(element.className.indexOf) && element.className.indexOf('arrow-button') !== -1 &&
  element.className.indexOf('remove') !== -1 &&
  element.parentElement !== null &&
  _.isFunction(element.parentElement.className.indexOf) && element.parentElement.className.indexOf('dlbItem') !== -1;

/**
 * Feeds analyzer, the most complicated analyzer
 */
export class AdCustomizerIffAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({
    deps: {
      config,
      instrumentation,
      i18n,
      jsDataCampaign,
      edmCampaign,
      audienceSelectorLegacyViewManager,
      perfMarker,
    },
    controller,
  }) {
    super({
      name: 'iff',
      config,
      priority: 2000,
      instrumentation,
      i18n,
      activityName: 'iffEditor',
      controller,
    });

    this.jsDataCampaign = jsDataCampaign;
    this.edmCampaign = edmCampaign;

    this.audienceSelectorLegacyViewManager =
      audienceSelectorLegacyViewManager || new AudienceSelectorLegacyViewManager({ perfMarker });
    this.onEditorSave = this.onEditorSave.bind(this);
    this.onEditorCancel = this.onEditorCancel.bind(this);

    const { IsNewAudienceEnabled, IsIFFAudienceSyntaxUpdateEnabled } = config.get('Permissions') || {};
    this.IsNewAudienceEnabled = IsNewAudienceEnabled;
    this.IsIFFAudienceSyntaxUpdateEnabled = IsIFFAudienceSyntaxUpdateEnabled;
  }

  formatExpression({
    audienceList,
    buildCompleteExpression = false,
    defaultText,
    insertText,
    source,
    startIndex,
    type,
    value,
  }) {
    let actualType = '';
    let conditionText = '';
    if (type === IF_FUNCTION_TYPE.AUDIENCE_IN) {
      actualType = this.IsIFFAudienceSyntaxUpdateEnabled ? 'audience IN ' : 'audience in';
      conditionText = `(${audienceList.map(escapeIffAudienceName).join(',')})`;
    } else if (buildCompleteExpression || type === IF_FUNCTION_TYPE.DEVICE_MOBILE) {
      actualType = 'device=mobile';
    }

    const endIndex = startIndex + source.length;
    const beginning = value.substring(0, startIndex);
    const end = value.substring(endIndex);
    const expressionStart = '{=IF(';
    const insertTextPart = escapeIffInsertText(insertText);
    const defaultTextPart = _.isEmpty(defaultText) ? '' : (`:${escapeIffDefaultText(defaultText)}`);
    const newValue = `${beginning}${expressionStart}${actualType}${conditionText},${insertTextPart})${defaultTextPart}}${end}`;
    const cursorPosition = buildCompleteExpression ?
      newValue.length - end.length :
      beginning.length + expressionStart.length;

    return {
      newValue,
      cursorPosition,
    };
  }

  /**
   * Gets the menu item
   * @private
   * @returns {Object} the menu item description
   */
  getMenuItem({
    startIndex, rule, target, updateAndCloseCallback,
  }) {
    return {
      name: this.i18n.getString('Picker_IFFunction_Label'),
      description: this.i18n.getString('Picker_IFFunction_Description'),
      onClick: (event) => {
        const {
          source,
          insertText = 'insert text',
          defaultText = 'default text',
          type,
          audienceList = [],
        } = rule;

        const { newValue, cursorPosition } = this.formatExpression({
          audienceList,
          buildCompleteExpression: false,
          defaultText,
          insertText,
          source,
          startIndex,
          type,
          value: target.value,
        });

        updateAndCloseCallback({
          newValue,
          cursorPosition,
          isSelectedByUser: true,
          event,
        });
      },
    };
  }

  getSyntax(/* analyzeResult */) {
    // {=IF(Condition, insert text):default text}
    const prefix = this.i18n.getString('AdCustomizers_Editor_Syntax_Prefix_Label');
    const condition = this.i18n.getString('IffEditor_Condition');
    const insertText = this.i18n.getString('IffEditor_InsertText');
    const defaultText = this.i18n.getString('IffEditor_DefaultText');
    const syntax = `${prefix} {=IF(${condition}, ${insertText}):${defaultText}}`;
    return (
      <div className="iff-syntax">
        <span>{syntax}
        </span>
        <HelpIcon
          topic="pop_BA_NewAdCreate_IFfunction_Syntax"
          ariaLabel={this.i18n.getString('Click to learn more about {{item}}', { item: this.i18n.getString('AdCustomizer_Syntax_Syntax') })}
        />
      </div>);
  }

  getStartIndex(value, cursorIndex) {
    return value.lastIndexOf('{', cursorIndex);
  }

  needEditor(analyzeResult) {
    return !!analyzeResult;
  }

  getEditor({
    target, analyzeResult, updateAndCloseCallback, /* remainingCountCallBack, */
  }) {
    const {
      source,
      type,
      audienceList,
      insertText,
      defaultText,
    } = analyzeResult;
    const editorProps = {
      i18n: this.i18n,
      source,
      type,
      audienceList,
      insertText,
      defaultText,
      audienceTargetsView: this.audienceSelectorLegacyViewManager.audienceTargetsView,
      onSave: _.partial(this.onEditorSave, {
        target, analyzeResult, updateAndCloseCallback,
      }),
      onCancel: _.partial(this.onEditorCancel, {
        updateAndCloseCallback,
      }),
      IsNewAudienceEnabled: this.IsNewAudienceEnabled,
    };
    return (<IffEditor key={_.uniqueId()} {...editorProps} />);
  }

  onEditorSave({
    target, analyzeResult, updateAndCloseCallback,
  }, eventData) {
    const {
      source,
      startIndex,
    } = analyzeResult;
    const {
      insertText,
      defaultText,
      type,
      audienceList,
      event,
    } = eventData;

    const { newValue, cursorPosition } = this.formatExpression({
      audienceList,
      buildCompleteExpression: true,
      defaultText,
      insertText,
      source,
      startIndex,
      type,
      value: target.value,
    });

    updateAndCloseCallback({
      newValue,
      cursorPosition,
      isSelectedByUser: true,
      event,
    });
  }

  onEditorCancel({ updateAndCloseCallback }/* , eventData */) {
    updateAndCloseCallback({ isCancel: true });
  }

  /**
   * Returns `true` if click was not on an audience selection or removal buttons.
   * The default logic in the controller, which traces the event's target up
   * the UI tree to see if it was under one of the controller's managed elements
   * doesn't work with audiences, because by the time the click handler of controller
   * is called, the audience selector has already removed the arrow button from DOM.
   * @param {Object} event the click event.
   * @returns {Boolean} Returns `true` if click was not on an audience selection or removal buttons.
   * Otherwise, `false`.
   */
  isClickedAway(event) {
    return !isSelectArrow(event.target) &&
      !isRemoveArrow(event.target);
  }

  /**
  * analyze input
  * @param {Object} options - options for parametr
  * @param {Object} options.event - event
  * @param {func} options.updateAction - update call back
  * @param {Object} options.i18n - whether this is selected option
  * @param { Object} options.container -- container to show the picker
  * @returns {Object} promise which will resolve the result
  */
  analyzeInput({
    text, cursorIndex, target, updateAndCloseCallback,
  }) {
    // const processedText = text ? text.toLowerCase() : '';
    const startIndex = this.getStartIndex(text, cursorIndex);

    if (startIndex === -1 || startIndex === cursorIndex) {
      return Promise.resolve(null);
    }

    const rule = parseRule({
      text,
      cursorIndex,
      startIndex,
    });

    // The parser should have found an exression on the left from
    // the cursor AND cursor should be within the matched part of the string
    if (rule && cursorIndex <= startIndex + rule.source.length &&
      // AND cursor shouldn't be at the expression's closing "}"
      !(
        rule.isComplete &&
        cursorIndex === startIndex + rule.source.length
      ) &&
      // AND either we have found first significant character (isStarted)
      // or we are not past two characters after startIndex, inclusive
      (rule.isStarted || cursorIndex <= startIndex + 2)
    ) {
      return Promise.resolve(_.defaults({
        itemsToDisplay: [this.getMenuItem({
          startIndex,
          rule,
          target,
          updateAndCloseCallback,
        })],
        match: rule.isComplete,
        startIndex,
      }, rule));
    }

    return Promise.resolve(null);
  }

  getCharCountExcludingAdCustomizers(text, isComplete) {
    let processedText = text;
    let charCount = 0;
    const rule = parseRule({
      text: processedText,
      startIndex: 0,
    });

    if (rule && rule.source && (!isComplete || rule.isComplete)) {
      const {
        insertText = '',
        defaultText = '',
      } = rule;
      if (rule.isComplete) {
        charCount += Math.max(insertText.length, defaultText.length);
      } else {
        charCount += rule.source.length;
      }
      processedText = processedText.replace(rule.source, '');
    }

    return {
      processedText,
      charCount,
    };
  }

  evaluateRule(text) {
    let processedText = text;
    let rule = parseRule({
      text: processedText,
      startIndex: 0,
    });

    while (rule && rule.source) {
      if (rule.isComplete) {
        const {
          insertText = '',
        } = rule;
        processedText = processedText.replace(rule.source, insertText);
      }

      rule = parseRule({
        text: processedText,
        startIndex: rule.index + rule.source.length,
      });
    }

    return Promise.resolve(processedText);
  }
}
